import React, { useState } from 'react';
import {
  Type,
  spacer12,
  spacer8,
  spacer4,
  cssColorValue,
  TextLink,
} from '@spotify-internal/encore-web';
import { useT } from 'i18n/hooks/useT';
import styled from 'styled-components';
import { useFetchSpotifyAccount } from 'client/hooks/spotifyConnect/useFetchSpotifyAccount';
import { CenteredLoadingIndicator } from '@spotify-internal/anchor-ds';
import { SpotifyConnectAvatarIcon } from './SpotifyConnectAvatarIcon';
import { SpotifyAccountDisconnectModal } from './SpotifyAccountDisconnectModal';
import { SpotifyConnectModal } from './SpotifyConnectModal';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid ${cssColorValue('decorativeSubdued')};
  padding: ${spacer4} ${spacer12};
  border-radius: ${spacer8};
  box-shadow: 0px 2px 6px ${cssColorValue('decorativeSubdued')};
`;

const AvatarIconWrapper = styled.span`
  margin: ${spacer12};
`;

const ChangeTextLinkWrapper = styled.div`
  flex: 1;
  text-align: end;
  padding-right: ${spacer8};
  text-decoration: underline;
`;

export const SpotifyAccountCard = () => {
  const t = useT();
  const {
    data: accountInfo,
    status: spotifyAccountStatus,
    errorStatus,
    refetch: refetchSpotifyAccount,
  } = useFetchSpotifyAccount();

  const [{ url: avatarUrl } = { url: undefined }] = accountInfo?.images ?? [];
  const displayName = accountInfo?.displayName ?? 'Display Name';
  const [showDisconnectModal, setShowDisconnectModal] = useState<boolean>(
    false
  );
  const [
    showSpotifyConnectModal,
    setShowSpotifyConnectModal,
  ] = useState<boolean>(false);
  const isLoading = spotifyAccountStatus === 'loading';

  if (isLoading) {
    return <CenteredLoadingIndicator />;
  }

  if (!accountInfo || !!errorStatus) {
    return (
      <>
        <Type semanticColor="textBase">
          {t(
            'SpotifyAccountCard.',
            "You haven't linked a Spotify account yet. ",
            'Informing the user that they need to link a Spotify account to use this feature.'
          )}
          <Type weight="bold">
            <TextLink
              semanticColor="textAnnouncement"
              onClick={() => {
                setShowSpotifyConnectModal(true);
              }}
              as="button"
              type="button"
            >
              {t(
                'SpotifyAccountCard.connectSpotifyAccountButton',
                'Link your account',
                'Button to link a Spotify account'
              )}
            </TextLink>
          </Type>
        </Type>
        {showSpotifyConnectModal && (
          <SpotifyConnectModal
            onComplete={() => {
              setShowSpotifyConnectModal(false);
              refetchSpotifyAccount();
            }}
          />
        )}
      </>
    );
  }

  return (
    <Wrapper>
      <AvatarIconWrapper>
        <SpotifyConnectAvatarIcon
          displayName={displayName}
          avatarUrl={avatarUrl}
          size={50}
        />
      </AvatarIconWrapper>
      <Type variant="violaBold">{displayName}</Type>
      <ChangeTextLinkWrapper>
        <TextLink
          as="button"
          onClick={() => setShowDisconnectModal(true)}
          semanticColor="textPositive"
          type="button"
        >
          {t('Button.Change', 'Change', 'Change your settings')}
        </TextLink>
      </ChangeTextLinkWrapper>
      {showDisconnectModal && (
        <SpotifyAccountDisconnectModal
          onClose={() => setShowDisconnectModal(false)}
        />
      )}
    </Wrapper>
  );
};
